"use client";

import { useState } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import LocationMapPicker from "@/components/maps/LocationMapPicker";
import { IYKChipSelector } from "@/components/iyk/IYKChipSelector";
import { PrerequisiteSelector } from "@/components/prerequisites/PrerequisiteSelector";
import { TemplateCreationModal } from "@/components/TemplateCreationModal";
import { PoapEventInput } from "@/components/poap/PoapEventInput";
import { CampaignImageUpload } from "@/components/campaigns/CampaignImageUpload";
import type { ClueFormData, AtomicTemplateOption, CampaignFormData, CreatedTemplate, TenantColors } from "./types";
import type { TemplateInfo } from "@/lib/claims/eligibility-types";

interface LocationsStepProps {
  clues: ClueFormData[];
  campaignType?: "SINGLE_LOCATION" | "MULTI_LOCATION";
  onClueChange: (index: number, field: keyof ClueFormData, value: string | number | boolean | string[]) => void;
  onLocationChange: (index: number, locationData: {
    address: string;
    latitude?: number;
    longitude?: number;
    radiusMiles?: number | null;
    hideAddress?: boolean;
  }) => void;
  onAddClue: () => void;
  onRemoveClue: (index: number) => void;
  atomicTemplates?: AtomicTemplateOption[];
  templatesLoading?: boolean;
  campaignId?: string | null;
  onTemplateCreated?: (template: CreatedTemplate) => void;
  campaignPrerequisites?: CampaignFormData["prerequisites"];
  tenantColors?: TenantColors | null;
  chain?: string;
}

export function LocationsStep({
  clues,
  campaignType = "MULTI_LOCATION",
  onClueChange,
  onLocationChange,
  onAddClue,
  onRemoveClue,
  atomicTemplates = [],
  templatesLoading = false,
  campaignId,
  onTemplateCreated,
  campaignPrerequisites,
  tenantColors,
  chain,
}: LocationsStepProps) {
  const isSingleLocation = campaignType === "SINGLE_LOCATION";
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [activeClueIndex, setActiveClueIndex] = useState<number | null>(null);

  const accentButtonStyle = tenantColors
    ? { backgroundColor: tenantColors.accentColor, borderColor: tenantColors.accentColor, color: '#FFFFFF' }
    : undefined;

  const getSelectedTemplate = (atomicTemplateId?: string) => {
    if (!atomicTemplateId) return undefined;
    return atomicTemplates.find(t => t.id === atomicTemplateId);
  };

  const handleOpenTemplateModal = (clueIndex: number) => {
    setActiveClueIndex(clueIndex);
    setShowTemplateModal(true);
  };

  const handleTemplateCreated = (template: CreatedTemplate) => {
    if (onTemplateCreated) {
      onTemplateCreated(template);
    }
    if (activeClueIndex !== null) {
      onClueChange(activeClueIndex, "atomicTemplateId", template.id);
    }
    setShowTemplateModal(false);
    setActiveClueIndex(null);
  };

  const availablePrerequisiteTemplates: TemplateInfo[] = atomicTemplates
    .filter(t => t.onChainTemplateId !== null && t.status === "CREATED")
    .map(t => ({
      templateId: t.onChainTemplateId as string,
      name: t.name,
      imageUrl: t.imageUrl,
      collectionName: "campaign",
    }));

  const hasCampaignPrerequisites = campaignPrerequisites?.enabled &&
    (campaignPrerequisites.templateIds?.length ?? 0) > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">
          {isSingleLocation ? "Event Configuration" : "Locations"}
        </h2>
        {!isSingleLocation && (
          <Button
            onClick={onAddClue}
            variant={accentButtonStyle ? undefined : "outline"}
            style={accentButtonStyle}
          >
            Add Location
          </Button>
        )}
      </div>

      {isSingleLocation && (
        <p className="text-sm text-gray-500">
          Configure the NFC chip and digital asset for your event. Location details are inherited from the campaign.
        </p>
      )}

      {clues.map((clue, index) => (
        <div key={clue.id || index} className="border border-gray-300 rounded-lg p-4">
          {!isSingleLocation && (
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">
                Location {index + 1}
              </h3>
              {clues.length > 1 && (
                <Button
                  onClick={() => onRemoveClue(index)}
                  variant="destructive"
                  size="sm"
                >
                  Remove
                </Button>
              )}
            </div>
          )}

          <div className="space-y-4">
            {!isSingleLocation && (
              <>
                <div>
                  <Label htmlFor={`clue-title-${index}`}>Title *</Label>
                  <Input
                    id={`clue-title-${index}`}
                    value={clue.title}
                    onChange={(e) => onClueChange(index, "title", e.target.value)}
                    className="mt-1"
                    placeholder="Location name"
                  />
                </div>

                <div>
                  <Label htmlFor={`clue-description-${index}`}>Description *</Label>
                  <textarea
                    id={`clue-description-${index}`}
                    rows={2}
                    value={clue.description}
                    onChange={(e) => onClueChange(index, "description", e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Clue or instructions for participants"
                  />
                </div>

                <CampaignImageUpload
                  id={`clue-image-${index}`}
                  label="Location Photo"
                  value={clue.imageUrl}
                  onChange={(url) => onClueChange(index, "imageUrl", url)}
                  description="Upload a photo for this location"
                  campaignId={campaignId || undefined}
                />

                <div>
                  <Label>Location *</Label>
                  <LocationMapPicker
                    initialLocation={{
                      address: clue.address,
                      latitude: clue.latitude,
                      longitude: clue.longitude,
                      radiusMiles: clue.radiusMiles,
                      hideAddress: clue.hideAddress
                    }}
                    onLocationChange={(locationData) => onLocationChange(index, locationData)}
                    className="mt-2"
                    showMapControl={{
                      id: `clue-show-map-${index}`,
                      checked: clue.showMap !== false,
                      onChange: (checked) => onClueChange(index, "showMap", checked),
                    }}
                  />
                </div>
              </>
            )}

            <IYKChipSelector
              value={clue.iykChipId}
              onChange={(chipId) => onClueChange(index, "iykChipId", chipId)}
              label="IYK Chip"
              placeholder="Select a chip for this location"
            />

            {chain === 'POAP' ? (
              <PoapEventInput
                clueIndex={index}
                poapEventId={clue.poapEventId || ""}
                onChange={(eventId) => onClueChange(index, "poapEventId", eventId)}
                clueId={clue.id}
              />
            ) : (
              <div>
                <Label htmlFor={`clue-atomic-template-${index}`}>NFT Template (Mint on Claim)</Label>
                <div className="flex gap-3 items-start mt-1">
                  <div className="flex-1">
                    {templatesLoading ? (
                      <div className="p-2 text-sm text-gray-500 bg-gray-50 rounded-md">
                        Loading templates...
                      </div>
                    ) : atomicTemplates.length > 0 ? (
                      <div className="flex gap-2">
                        <select
                          id={`clue-atomic-template-${index}`}
                          value={clue.atomicTemplateId || ""}
                          onChange={(e) => onClueChange(index, "atomicTemplateId", e.target.value)}
                          className="block flex-1 rounded-md border border-gray-300 bg-white text-gray-900 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select a template...</option>
                          {atomicTemplates.map((template) => {
                            const statusLabel = template.status === "PENDING" ? " [Draft]" :
                                                template.status === "CREATING" ? " [Syncing...]" :
                                                template.status === "FAILED" ? " [Failed]" : "";
                            const displayId = template.onChainTemplateId
                              ? `ID: ${template.onChainTemplateId}`
                              : "Not yet on blockchain";
                            return (
                              <option key={template.id} value={template.id}>
                                {template.name}{statusLabel} ({displayId})
                              </option>
                            );
                          })}
                        </select>
                        <Button
                          type="button"
                          variant={accentButtonStyle ? undefined : "outline"}
                          size="sm"
                          onClick={() => handleOpenTemplateModal(index)}
                          className="flex-shrink-0"
                          style={accentButtonStyle}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          New
                        </Button>
                      </div>
                    ) : (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <p className="text-sm text-amber-800 mb-2">
                          No templates available yet. Create one to enable NFT minting for this location.
                        </p>
                        <Button
                          type="button"
                          variant={accentButtonStyle ? undefined : "outline"}
                          size="sm"
                          onClick={() => handleOpenTemplateModal(index)}
                          style={accentButtonStyle}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Create Template
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Select a template created in Digital Assets. NFT will be minted when participant claims.
                    </p>
                  </div>
                  {clue.atomicTemplateId && (
                    <div className="flex-shrink-0">
                      {(() => {
                        const selectedTemplate = getSelectedTemplate(clue.atomicTemplateId);
                        if (selectedTemplate?.videoUrl) {
                          return (
                            <video
                              src={selectedTemplate.videoUrl}
                              className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                              muted
                              loop
                              playsInline
                              autoPlay
                              crossOrigin="anonymous"
                            />
                          );
                        }
                        if (selectedTemplate?.imageUrl) {
                          return (
                            <Image
                              src={selectedTemplate.imageUrl}
                              alt={selectedTemplate.name}
                              width={64}
                              height={64}
                              className="object-cover rounded-lg border border-gray-200"
                              unoptimized
                            />
                          );
                        }
                        return (
                          <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                            <span className="text-gray-400 text-xs">No img</span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Prerequisites Configuration */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">
                  Claiming Prerequisites
                </Label>

                {hasCampaignPrerequisites && (
                  <div className="flex items-center gap-2">
                    <input
                      id={`clue-use-campaign-prereqs-${index}`}
                      type="checkbox"
                      checked={clue.usesCampaignPrerequisites !== false}
                      onChange={(e) => onClueChange(index, "usesCampaignPrerequisites", e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Label
                      htmlFor={`clue-use-campaign-prereqs-${index}`}
                      className="text-sm text-gray-600"
                    >
                      Use campaign prerequisites
                    </Label>
                    <span className="text-xs text-gray-400">
                      ({campaignPrerequisites?.templateIds?.length ?? 0} NFT{(campaignPrerequisites?.templateIds?.length ?? 0) !== 1 ? "s" : ""} required)
                    </span>
                  </div>
                )}

                {hasCampaignPrerequisites && clue.usesCampaignPrerequisites !== false && (
                  <div className="ml-6 p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-700">
                      This location uses the campaign-level prerequisites. Users must own the{" "}
                      {campaignPrerequisites?.logicType === "OR" ? "any one of" : "all"}{" "}
                      {campaignPrerequisites?.templateIds?.length ?? 0} specified NFT{(campaignPrerequisites?.templateIds?.length ?? 0) !== 1 ? "s" : ""}{" "}
                      to claim this reward.
                    </p>
                  </div>
                )}

                {(!hasCampaignPrerequisites || clue.usesCampaignPrerequisites === false) && (
                  <div className={hasCampaignPrerequisites ? "ml-6" : ""}>
                    <PrerequisiteSelector
                      selectedTemplates={clue.prerequisiteTemplateIds ?? []}
                      onSelectionChange={(templateIds) =>
                        onClueChange(index, "prerequisiteTemplateIds", templateIds)
                      }
                      logicType={clue.prerequisiteLogicType ?? "AND"}
                      onLogicTypeChange={(logicType) =>
                        onClueChange(index, "prerequisiteLogicType", logicType)
                      }
                      availableTemplates={availablePrerequisiteTemplates}
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      {!hasCampaignPrerequisites
                        ? "Configure which NFTs users must own before claiming this reward. Leave empty for no prerequisites."
                        : "Configure custom prerequisites for this location instead of using campaign defaults."}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor={`clue-slug-${index}`}>URL Slug</Label>
              <Input
                id={`clue-slug-${index}`}
                value={clue.slug}
                onChange={(e) => onClueChange(index, "slug", e.target.value)}
                className="mt-1"
                placeholder="Auto-generated from title"
              />
            </div>
          </div>
        </div>
      ))}

      <TemplateCreationModal
        isOpen={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false);
          setActiveClueIndex(null);
        }}
        onCreated={handleTemplateCreated}
        campaignId={campaignId || undefined}
      />
    </div>
  );
}
